import { cloneDeep, isUndefined } from 'lodash-es'
import create from 'zustand'
import { combine } from 'zustand/middleware'
import { MutationKey } from '@tanstack/react-query'

export type InteractionState = 'running' | 'success' | 'failed'

export interface InteractionEntry {
  id: string
  mutationKey: MutationKey
  title: string
  description: string
  state: InteractionState
  errorResponse: unknown | null
  createdAt: string
}

interface UIStoreState {
  reducedLogo: boolean
  editorNavOpen: boolean
  portalRootLeft: HTMLDivElement | null
  portalRootCenter: HTMLDivElement | null
  portalRootRight: HTMLDivElement | null
  portalRootAiActions: HTMLDivElement | null
  interactions: InteractionEntry[]
  // Triggered from the Navbar (outside the editor's own, context-scoped
  // control store) and read by a dialog mounted in the editor tree, so
  // opening it doesn't depend on the navbar menu -- which closes, and would
  // otherwise unmount anything rendered inside it -- staying open.
  isEditProjectDialogOpen: boolean
  // The AI chat thread per project for this session. Chosen by the client
  // (an AG-UI thread id) and handed to the backend on the first turn, which
  // binds it to the user and project; kept here so the chat can be
  // closed and reopened without losing the conversation.
  aiChatThreadIdByProject: Record<string, string>
  // The planar elements the AI chat marked in the scene per project
  // (`mark_elements`) or the user marked by clicking an element link, until
  // the user removes the marking (the chat's chip) or the agent marks again.
  // Read by the editor's AIChatSceneBridge.
  aiChatMarkedElementGuidsByProject: Record<string, string[]>
  // Projects whose marking the user hid (the chip's eye) without removing it;
  // a new marking shows again.
  aiChatMarkingHiddenByProject: Record<string, boolean>
  // The model picked for a chat thread in this session (a catalogue id from
  // `GET /agent-runs/status`); absent means the catalogue's default or, for
  // a reopened thread, the model of its latest turn.
  aiChatModelByThread: Record<string, string>
  // Threads whose changes run without asking ("Änderungen automatisch
  // freigeben"), for this session only: a reload asks again.
  aiChatAutoApproveByThread: Record<string, boolean>
  // Whether the AI chat is shown (in the right drawer). The chat is
  // registered in the navbar, outside the editor tree; the drawer offers it
  // a portal root (`AIChatDrawerSlot`) while it is open.
  aiChatOpen: boolean
  portalRootAiChat: HTMLDivElement | null
}

const initialState: UIStoreState = {
  reducedLogo: false,
  editorNavOpen: false,
  portalRootLeft: null,
  portalRootCenter: null,
  portalRootRight: null,
  portalRootAiActions: null,
  interactions: [],
  isEditProjectDialogOpen: false,
  aiChatThreadIdByProject: {},
  aiChatMarkedElementGuidsByProject: {},
  aiChatMarkingHiddenByProject: {},
  aiChatModelByThread: {},
  aiChatAutoApproveByThread: {},
  aiChatOpen: false,
  portalRootAiChat: null,
}

const useUIStore = create(
  combine(cloneDeep(initialState), set => ({
    toggleReducedLogo: (value?: boolean) =>
      set(state => ({ reducedLogo: !isUndefined(value) ? value : !state.reducedLogo })),
    toggleEditorNavOpen: (value?: boolean) =>
      set(state => ({ editorNavOpen: !isUndefined(value) ? value : !state.editorNavOpen })),
    setPortalRootLeft: (portalRootLeft: HTMLDivElement | null) => set({ portalRootLeft }),
    setPortalRootCenter: (portalRootCenter: HTMLDivElement | null) => set({ portalRootCenter }),
    setPortalRootRight: (portalRootRight: HTMLDivElement | null) => set({ portalRootRight }),
    setPortalRootAiActions: (portalRootAiActions: HTMLDivElement | null) =>
      set({ portalRootAiActions }),
    setIsEditProjectDialogOpen: (isEditProjectDialogOpen: boolean) =>
      set({ isEditProjectDialogOpen }),
    setAiChatMarkedElementGuids: (projectId: string, guids: string[]) =>
      set(state => ({
        aiChatMarkedElementGuidsByProject: {
          ...state.aiChatMarkedElementGuidsByProject,
          [projectId]: guids,
        },
        aiChatMarkingHiddenByProject: {
          ...state.aiChatMarkingHiddenByProject,
          [projectId]: false,
        },
      })),
    toggleAiChatMarkedElementGuid: (projectId: string, guid: string) =>
      set(state => {
        const guids = state.aiChatMarkedElementGuidsByProject[projectId] ?? []
        return {
          aiChatMarkedElementGuidsByProject: {
            ...state.aiChatMarkedElementGuidsByProject,
            [projectId]: guids.includes(guid) ? guids.filter(g => g !== guid) : [...guids, guid],
          },
          // Clicking a link shows what it marks.
          aiChatMarkingHiddenByProject: {
            ...state.aiChatMarkingHiddenByProject,
            [projectId]: false,
          },
        }
      }),
    toggleAiChatMarkingHidden: (projectId: string) =>
      set(state => ({
        aiChatMarkingHiddenByProject: {
          ...state.aiChatMarkingHiddenByProject,
          [projectId]: !state.aiChatMarkingHiddenByProject[projectId],
        },
      })),
    setAiChatThreadId: (projectId: string, threadId: string) =>
      set(state => ({
        aiChatThreadIdByProject: { ...state.aiChatThreadIdByProject, [projectId]: threadId },
      })),
    setAiChatModel: (threadId: string, model: string) =>
      set(state => ({
        aiChatModelByThread: { ...state.aiChatModelByThread, [threadId]: model },
      })),
    setAiChatAutoApprove: (threadId: string, autoApprove: boolean) =>
      set(state => ({
        aiChatAutoApproveByThread: { ...state.aiChatAutoApproveByThread, [threadId]: autoApprove },
      })),
    setAiChatOpen: (aiChatOpen: boolean) => set({ aiChatOpen }),
    setPortalRootAiChat: (portalRootAiChat: HTMLDivElement | null) => set({ portalRootAiChat }),
    addInteraction: (interaction: InteractionEntry) =>
      set(state => ({ interactions: [...state.interactions, interaction] })),
    updateInteraction: (
      id: string,
      patch: Partial<Pick<InteractionEntry, 'state' | 'errorResponse'>>,
    ) =>
      set(state => ({
        interactions: state.interactions.map(item =>
          item.id === id ? { ...item, ...patch } : item,
        ),
      })),
  })),
)

export default useUIStore
